import React from 'react';

import i18n from 'util/i18n';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import DateRange from 'components/Form/DateRange';
import Textarea from 'components/Form/Textarea';
import Branch from 'components/Form/Branch';
import Text from 'components/Form/Text';
import { SubstanceDrugsPrescriptionItemModel } from 'forms/sections/substance/drugs/prescription/types';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate, CommonOnError, CommonScrollIntoView, CommonRequired
} from 'types/props';
import Comments from 'components/Form/Comments';
import { isPVQForm } from 'helpers/form';
import Dropdown from 'components/Form/Dropdown';
import { criminalJusticeorPublicSafetyPosition } from 'constants/enums/substanceOptions';

interface Props extends ValidationElementProps, Partial<SubstanceDrugsPrescriptionItemModel> {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  className?: string
  scrollIntoView?: CommonScrollIntoView
  required?: CommonRequired
  attribute?: string
  requireDrugWhileSafety?: boolean
  requireDrugWithClearance?: boolean
  formType?: string
}

export default class PrescriptionUse extends ValidationElement<Props> {
  update = (updateValues: Partial<SubstanceDrugsPrescriptionItemModel>) => {
    if (this.props.onUpdate) {
      this.props.onUpdate({
        PrescriptionName: this.props.PrescriptionName,
        InvolvementDates: this.props.InvolvementDates,
        Reason: this.props.Reason,
        UseWhileEmployed: this.props.UseWhileEmployed,
        UseWithClearance: this.props.UseWithClearance,
        Comments: this.props.Comments,
        HowManyTimes: this.props.HowManyTimes,
        UseWhileEmployedPosition: this.props.UseWhileEmployedPosition,
        UseWhileEmployedExplanation: this.props.UseWhileEmployedExplanation,
        UseWithClearanceExplanation: this.props.UseWithClearanceExplanation,
        UseInFuture: this.props.UseInFuture,
        UseInFutureExplanation: this.props.UseInFutureExplanation,
        ...updateValues,
      });
    }
  };

  updatePrescriptionName = (values: SubstanceDrugsPrescriptionItemModel['PrescriptionName']) => {
    this.update({ PrescriptionName: values });
  };

  updateInvolvementDates = (values: SubstanceDrugsPrescriptionItemModel['InvolvementDates']) => {
    this.update({ InvolvementDates: values });
  };

  updateReason = (values: SubstanceDrugsPrescriptionItemModel['Reason']) => {
    this.update({ Reason: values });
  };

  updateUseWhileEmployed = (values: SubstanceDrugsPrescriptionItemModel['UseWhileEmployed']) => {
    this.update({ UseWhileEmployed: values });
  };

  updateUseWithClearance = (values: SubstanceDrugsPrescriptionItemModel['UseWithClearance']) => {
    this.update({ UseWithClearance: values });
  };

  updateComments = (values: SubstanceDrugsPrescriptionItemModel['Comments']) => {
    this.update({ Comments: values });
  };

  updateHowManyTimes = (values: SubstanceDrugsPrescriptionItemModel['HowManyTimes']) => {
    this.update({ HowManyTimes: values });
  };

  updateUseWhileEmployedPosition = (values: SubstanceDrugsPrescriptionItemModel['UseWhileEmployedPosition']) => {
    this.update({ UseWhileEmployedPosition: values });
  };

  updateUseWhileEmployedExplanation = (values: SubstanceDrugsPrescriptionItemModel['UseWhileEmployedExplanation']) => {
    this.update({ UseWhileEmployedExplanation: values });
  };

  updateUseWithClearanceExplanation = (values: SubstanceDrugsPrescriptionItemModel['UseWithClearanceExplanation']) => {
    this.update({ UseWithClearanceExplanation: values });
  };

  updateUseInFuture = (values: SubstanceDrugsPrescriptionItemModel['UseInFuture']) => {
    this.update({ UseInFuture: values });
  };

  updateUseInFutureExplanation = (values: SubstanceDrugsPrescriptionItemModel['UseInFutureExplanation']) => {
    this.update({ UseInFutureExplanation: values });
  };

  render() {
    const { requireDrugWhileSafety, requireDrugWithClearance } = this.props;
    const attribute = `List.items[${this.props.$index}].Item`;
    const isPvq = isPVQForm(this.props.formType);
    return isPvq ? (
      <div className="prescription-use">
        <Field
          attribute={`${attribute}.PrescriptionName`}
          title={i18n.t('substance.pvq.drugs.prescription.heading.prescriptionName')}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            {...this.props.PrescriptionName}
            attribute={`${attribute}.PrescriptionName`}
            name="PrescriptionName"
            className="prescription-name"
            onUpdate={this.updatePrescriptionName}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          attribute={`${attribute}.InvolvementDates`}
          adjustFor="daterange"
          // help="substance.drugs.prescription.help.involvementDates"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            {...this.props.InvolvementDates}
            allowPresent={false}
            attribute={`${attribute}.InvolvementDates`}
            className="involvement-dates"
            fromDateLabel={i18n.t('substance.pvq.drugs.prescription.heading.involvementDateFrom')}
            hideFromDay
            hideToDay
            minDateEqualTo
            name="InvolvementDates"
            onError={this.props.onError}
            onUpdate={this.updateInvolvementDates}
            required={this.props.required}
            toDateLabel={i18n.t('substance.pvq.drugs.prescription.heading.involvementDateTo')}
            usePvqFromDate
            usePvqToDate
          />
        </Field>

        <Field
          title={i18n.t('substance.pvq.drugs.prescription.heading.howManyTimes')}
          attribute={`${attribute}.HowManyTimes`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            {...this.props.HowManyTimes}
            name="HowManyTimes"
            attribute={`${attribute}.HowManyTimes`}
            className="howManyTimes"
            onUpdate={this.updateHowManyTimes}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          attribute={`${attribute}.Reason`}
          title={i18n.t('substance.pvq.drugs.prescription.heading.reason')}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            attribute={`${attribute}.Reason`}
            name="Reason"
            className="reason"
            {...this.props.Reason}
            onUpdate={this.updateReason}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Branch
          name="UseWithClearance"
          attribute={`${attribute}.UseWithClearance`}
          label={i18n.t('substance.pvq.drugs.prescription.heading.useWithClearance')}
          labelSize="h4"
          className="use-with-clearance"
          {...this.props.UseWithClearance}
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateUseWithClearance}
          scrollIntoView={this.props.scrollIntoView}
        />
        {this.props.UseWithClearance?.value === 'Yes' && (
          <Field
            title={i18n.t('substance.pvq.drugs.prescription.heading.useWithClearanceExplanation')}
            attribute={`${attribute}.UseWithClearanceExplanation`}
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              {...this.props.UseWithClearanceExplanation}
              name="useWhileEmployedExplanation"
              attribute={`${attribute}.UseWithClearanceExplanation`}
              className="useWithClearanceExplanation"
              onUpdate={this.updateUseWithClearanceExplanation}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        )}


        <Branch
          {...this.props.UseWhileEmployed}
          name="UseWhileEmployed"
          attribute={`${attribute}.UseWhileEmployed`}
          label={i18n.t('substance.pvq.drugs.prescription.heading.useWhileEmployed')}
          labelSize="h4"
          className="use-while-employed"
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateUseWhileEmployed}
          scrollIntoView={this.props.scrollIntoView}
        />

        {this.props.UseWhileEmployed?.value === 'Yes' && (
          <>

            <Field
              title={i18n.t('substance.pvq.drugs.prescription.heading.useWhileEmployedPosition')}
              attribute={`${attribute}.UseWhileEmployedPosition`}
              adjustFor="buttons"
              shrink
              className="no-margin-bottom"
            >
              <Dropdown
                attribute={`${attribute}.UseWhileEmployedPosition`}
                name="UseWhileEmployedPosition"
                className="UseWhileEmployedPosition"
                onUpdate={this.updateUseWhileEmployedPosition}
                onError={this.props.onError}
                value={this.props.UseWhileEmployedPosition?.value}
                required={this.props.required}
              >
                {criminalJusticeorPublicSafetyPosition.map((s) => {
                  return (<option key={s} value={s}>
                    {s}
                  </option>);
                })}
              </Dropdown>
            </Field>
            <Field
              title={i18n.t('substance.pvq.drugs.prescription.heading.useWhileEmployedExplanation')}
              attribute={`${attribute}.UseWhileEmployedExplanation`}
              scrollIntoView={this.props.scrollIntoView}
            >
              <Textarea
                {...this.props.UseWhileEmployedExplanation}
                name="useWhileEmployedExplanation"
                attribute={`${attribute}.UseWhileEmployedExplanation`}
                className="useWhileEmployedExplanation"
                onUpdate={this.updateUseWhileEmployedExplanation}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>
          </>
        )}

       
        <Branch
          {...this.props.UseInFuture}
          name="UseInFuture"
          attribute={`${attribute}.UseInFuture`}
          label={i18n.t('substance.pvq.drugs.prescription.heading.useInFuture')}
          labelSize="h4"
          className="use-in-future"
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateUseInFuture}
          scrollIntoView={this.props.scrollIntoView}
        />

        <Field
          title={i18n.t('substance.pvq.drugs.prescription.heading.useInFutureExplanation')}
          attribute={`${attribute}.UseInFutureExplanation`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            {...this.props.UseInFutureExplanation}
            name="UseInFutureExplanation"
            attribute={`${attribute}.UseInFutureExplanation`}
            className="useInFutureExplanation"
            onUpdate={this.updateUseInFutureExplanation}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

      </div>
    ) : (
      <div className="prescription-use">
        <Field
          attribute={`${attribute}.PrescriptionName`}
          title={i18n.t('substance.drugs.prescription.heading.prescriptionName')}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            attribute={`${attribute}.PrescriptionName`}
            name="PrescriptionName"
            className="prescription-name"
            {...this.props.PrescriptionName}
            onUpdate={this.updatePrescriptionName}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          attribute={`${attribute}.InvolvementDates`}
          title={i18n.t('substance.drugs.prescription.heading.involvementDates')}
          adjustFor="daterange"
          help="substance.drugs.prescription.help.involvementDates"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            attribute={`${attribute}.InvolvementDates`}
            name="InvolvementDates"
            className="involvement-dates"
            {...this.props.InvolvementDates}
            minDateEqualTo
            onUpdate={this.updateInvolvementDates}
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
          />
        </Field>

        <Field
          attribute={`${attribute}.Reason`}
          title={i18n.t('substance.drugs.prescription.heading.reason')}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            attribute={`${attribute}.Reason`}
            name="Reason"
            className="reason"
            {...this.props.Reason}
            onUpdate={this.updateReason}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        {requireDrugWhileSafety && (
          <Branch
            attribute={`${attribute}.UseWhileEmployed`}
            name="UseWhileEmployed"
            label={i18n.t('substance.drugs.prescription.heading.useWhileEmployed')}
            labelSize="h4"
            className="use-while-employed"
            {...this.props.UseWhileEmployed}
            onError={this.props.onError}
            onUpdate={this.updateUseWhileEmployed}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        )}

        {requireDrugWithClearance && (
          <Branch
            attribute={`${attribute}.UseWithClearance`}
            name="UseWithClearance"
            label={i18n.t('substance.drugs.prescription.heading.useWithClearance')}
            labelSize="h4"
            className="use-with-clearance"
            {...this.props.UseWithClearance}
            onError={this.props.onError}
            onUpdate={this.updateUseWithClearance}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        )}
        <Comments
          title={i18n.t('substance.comments.optional')}
          name="Comments"
          className="optional-comment"
          attribute="Comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          required={false}
        />
      </div>
    );
  }
}

PrescriptionUse.defaultProps = {
  UseWhileEmployed: {},
  UseWhileClearance: {},
  HowManyTimes: {},
  UseWhileEmployedPosition: {},
  UseWhileEmployedExplanation: {},
  UseWithClearanceExplanation: {},
  UseInFutureExplanation: {},
  onError: (value, arr) => arr,
};
